import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import React, { useState } from 'react';
import { match } from 'ts-pattern';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Caption,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DetailsSidebar,
  DetailsSidebarProvider,
  DetailsSidebarTrigger,
  EluveCardContent,
  EluveCardHeader,
  Icon,
  LinearLogo,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Stack,
  Tag,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { timestampToHumanReadable } from '@eluve/date-utils';
import { useSession } from '@eluve/session-helpers';
import {
  EluveAdminOnly,
  OpenInAdminButton,
  OpenInAppButton,
} from '@eluve/smart-blocks';
import { formatAppointmentTagLabel, isLinearIssueLink } from '@eluve/utils';

import { AddAppointmentReviewLinkDialogForm } from './AddAppointmentReviewLinkDialogForm';
import { AppointmentTags } from './AppointmentTags';
import {
  addLowQualityFactorMutation,
  appointmentAnnotationsFragment,
  appointmentLowQualityFactorsFragment,
  appointmentReviewLinksFragment,
  createAppointmentAnnotationMutation,
  removeAppointmentReviewLinkMutation,
  removeLowQualityFactorMutation,
} from './operations';
import { useAppointmentParams } from './useAppointmentParams';
import { useLowQualityFactors } from './useLowQualityFactors';

export const AppointmentAnnotationTools: React.FC<{}> = () => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [annotation, setAnnotation] = useState('');
  const [factorsOpen, setFactorsOpen] = useState(false);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [toolsPanelOpen, setToolsPanelOpen] = useState(true);
  const email = useSession().email;
  const toast = useToast();

  const { annotations } = useCompleteFragment({
    fragment: appointmentAnnotationsFragment,
    key: {
      id: appointmentId,
    },
  });

  const { low_quality_factors: selectedQualityFactors } = useCompleteFragment({
    fragment: appointmentLowQualityFactorsFragment,
    key: {
      id: appointmentId,
    },
  });

  const { review_links } = useCompleteFragment({
    fragment: appointmentReviewLinksFragment,
    key: {
      id: appointmentId,
    },
  });

  const allLowQualityFactors = useLowQualityFactors();

  const availableFactors = allLowQualityFactors.filter(
    (f) => !selectedQualityFactors.some((lqf) => lqf.factor === f.name),
  );

  const [removeAppointmentReviewLink] = useMutation(
    removeAppointmentReviewLinkMutation,
    {
      onError: () => toast.error('Error removing link.'),
      optimisticResponse: ({ id }) => ({
        deleteAppointmentReviewLinksByPk: {
          __typename: 'AppointmentReviewLinks' as const,
          id,
        },
      }),
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentReviewLinksFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            const removedFactor = data?.deleteAppointmentReviewLinksByPk;
            if (!existing || !removedFactor) {
              return existing;
            }

            return {
              ...existing,
              review_links: existing.review_links.filter(
                (l) => l.id !== removedFactor.id,
              ),
            };
          },
        );
      },
    },
  );

  const [createAppointmentAnnotation] = useMutation(
    createAppointmentAnnotationMutation,
    {
      onError: () => toast.error('Error saving annotation.'),
      optimisticResponse: ({ input }) => ({
        insertAppointmentAnnotationsOne: {
          __typename: 'AppointmentAnnotations' as const,
          id: 'temp-id',
          createdAt: new Date().toISOString(),
          annotation: {
            __typename: 'Annotations' as const,
            id: 'temp-id',
            annotation: input.annotation?.data.annotation ?? '',
          },
        },
      }),
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentAnnotationsFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing || !data?.insertAppointmentAnnotationsOne) {
              return existing;
            }

            const newAnnotation = data.insertAppointmentAnnotationsOne;

            return produce(existing, (draft) => {
              draft.annotations.push({
                ...newAnnotation,
                userEmail: email,
              });
            });
          },
        );
      },
    },
  );

  const [removeLowQualityFactor] = useMutation(removeLowQualityFactorMutation, {
    onError: () => toast.error('Error removing Low Quality Factor.'),
    optimisticResponse: ({ id }) => ({
      deleteAppointmentLowQualityFactorsByPk: {
        __typename: 'AppointmentLowQualityFactors' as const,
        id,
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentLowQualityFactorsFragment,
          key: {
            id: appointmentId,
          },
        },
        (existing) => {
          const removedFactor = data?.deleteAppointmentLowQualityFactorsByPk;
          if (!existing || !removedFactor) {
            return existing;
          }

          return {
            ...existing,
            low_quality_factors: existing.low_quality_factors.filter(
              (lqf) => lqf.id !== removedFactor.id,
            ),
          };
        },
      );
    },
  });

  const [addLowQualityFactor] = useMutation(addLowQualityFactorMutation, {
    onCompleted: () => toast.success('Low Quality Factor added.'),
    onError: () => toast.error('Error adding Low Quality Factor.'),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentLowQualityFactorsFragment,
          key: {
            id: appointmentId,
          },
        },
        (existing) => {
          const newFactor = data?.insertAppointmentLowQualityFactorsOne;
          if (!existing || !newFactor) {
            return existing;
          }

          return {
            ...existing,
            low_quality_factors: [...existing.low_quality_factors, newFactor],
          };
        },
      );
    },
  });

  const handleSaveAnnotation = () => {
    createAppointmentAnnotation({
      variables: {
        input: {
          tenantId,
          appointmentId,
          annotation: {
            data: {
              annotation,
            },
          },
        },
      },
    });
    setAnnotation('');
  };

  const handleAddLowQualityFactor = (factor: string) => {
    addLowQualityFactor({
      variables: {
        input: {
          tenantId,
          appointmentId,
          factor,
        },
      },
    });
  };

  return (
    <DetailsSidebarProvider overlayOnly>
      <DetailsSidebar title="Annotations" size="lg">
        <Stack className="p-8" gap={8}>
          <Stack gap={4}>
            {annotations.map((a) => (
              <Card className="w-full" key={a.id}>
                <EluveCardHeader>
                  <Stack wrap justify="between" gap={2} direction="horizontal">
                    <Text size="md">{a.userEmail}</Text>
                    <Text size="md">
                      {timestampToHumanReadable(
                        new Date(a.createdAt).getTime(),
                      )}
                    </Text>
                  </Stack>
                </EluveCardHeader>
                <EluveCardContent>{a.annotation.annotation}</EluveCardContent>
              </Card>
            ))}
          </Stack>
          <Stack gap={2}>
            <Textarea
              value={annotation}
              onChange={(e) => setAnnotation(e.target.value)}
            />
            <Button disabled={!annotation} onClick={handleSaveAnnotation}>
              Save
            </Button>
          </Stack>
        </Stack>
      </DetailsSidebar>
      <Card>
        <Collapsible
          open={toolsPanelOpen}
          onOpenChange={setToolsPanelOpen}
          className="group"
        >
          <EluveCardHeader>
            <Stack justify="between" gap={2} direction="horizontal">
              Tools
              <Stack gap={5} fullWidth={false} direction="horizontal">
                <Stack gap={2} direction="horizontal">
                  <EluveAdminOnly>
                    <>
                      <OpenInAdminButton
                        appointmentId={appointmentId}
                        tenantId={tenantId}
                        size="sm"
                      />
                      <OpenInAppButton
                        appointmentId={appointmentId}
                        tenantId={tenantId}
                        size="sm"
                      />
                    </>
                  </EluveAdminOnly>
                  <DetailsSidebarTrigger>
                    <Button variant="outline" size="sm">
                      <Icon name="Pencil" size="auto" />
                      {`Annotate (${annotations.length})`}
                    </Button>
                  </DetailsSidebarTrigger>
                </Stack>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Icon
                      name={toolsPanelOpen ? 'ChevronUp' : 'ChevronDown'}
                      size="auto"
                    />
                  </Button>
                </CollapsibleTrigger>
              </Stack>
            </Stack>
          </EluveCardHeader>
          <CollapsibleContent>
            <EluveCardContent>
              <Stack gap={2}>
                <Stack gap={4} direction="horizontal">
                  <Caption
                    inline
                    className="whitespace-nowrap"
                    color="tertiary"
                  >
                    Low Quality Factors:
                  </Caption>
                  <Stack wrap gap={2} direction="horizontal">
                    {selectedQualityFactors.map((lqf) => (
                      <Tag
                        key={lqf.factor}
                        color="negative"
                        size="s"
                        label={formatAppointmentTagLabel(lqf.factor)}
                        pill
                      >
                        <button
                          onClick={() =>
                            removeLowQualityFactor({
                              variables: {
                                id: lqf.id,
                              },
                            })
                          }
                          className="-m-2 -mr-2.5 size-8 cursor-pointer rounded-full border-8 border-transparent bg-clip-padding p-px hover:bg-bg-negative-strong-secondary hover:text-text-negative-strong"
                        >
                          <Icon name="X" size="sm" />
                        </button>
                      </Tag>
                    ))}
                    <Popover open={factorsOpen} onOpenChange={setFactorsOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={availableFactors.length === 0}
                        >
                          <Icon name="Plus" size="auto" />
                          Add
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="right"
                        align="start"
                        className="p-0"
                      >
                        <Command>
                          <CommandInput placeholder="Select factor" />
                          <CommandList>
                            <CommandGroup>
                              {availableFactors.map((f) => (
                                <CommandItem
                                  key={f.name}
                                  onSelect={() =>
                                    handleAddLowQualityFactor(f.name)
                                  }
                                >
                                  {formatAppointmentTagLabel(f.name)}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </Stack>
                </Stack>
                <Separator className="my-2" />
                <Stack gap={4} direction="horizontal">
                  <Caption inline color="tertiary">
                    Tags:
                  </Caption>
                  <AppointmentTags
                    tenantId={tenantId}
                    appointmentId={appointmentId}
                  />
                </Stack>
                <Separator className="my-2" />
                <Stack gap={4} direction="horizontal">
                  <Caption inline color="tertiary">
                    Links:
                  </Caption>
                  <Stack wrap gap={2} direction="horizontal">
                    {review_links.map((l) => (
                      <Stack
                        fullWidth={false}
                        key={l.link}
                        className="rounded-lg border border-border-primary bg-white px-2 text-text-primary hover:border-bg-tertiary"
                        gap={2}
                        direction="horizontal"
                      >
                        <a
                          className="flex items-center gap-2 hover:text-text-secondary"
                          href={l.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {match(isLinearIssueLink(l.link))
                            .with(true, () => <LinearLogo className="size-5" />)
                            .otherwise(() => (
                              <Icon name="ExternalLink" size="sm" />
                            ))}

                          {l.label ?? l.link}
                        </a>
                        <div
                          className="-m-2 -mr-2.5 size-8 cursor-pointer rounded-full border-8 border-transparent bg-clip-padding p-px hover:bg-text-primary hover:text-text-disabled"
                          onClick={() =>
                            removeAppointmentReviewLink({
                              variables: {
                                id: l.id,
                              },
                            })
                          }
                        >
                          <Icon name="X" size="sm" />
                        </div>
                      </Stack>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingLink(true)}
                    >
                      <Icon name="Plus" size="auto" />
                      Add
                    </Button>
                    <AddAppointmentReviewLinkDialogForm
                      isDialogOpen={isAddingLink}
                      setIsDialogOpen={setIsAddingLink}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </EluveCardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </DetailsSidebarProvider>
  );
};
